import { createSlice } from '@reduxjs/toolkit'
import * as Sentry from '@sentry/react'
import { useSelector } from 'react-redux'
import { clearCategories } from 'state/Categories'
import { clearInvoices } from 'state/Invoices'
import { Locale, SupportChat, useObjSelectorOrFetch } from 'utils'
import HTTP_CODE from 'utils/HTTP_CODE'
import { clearAccount, setAccount } from './Account'
import API from './API'
import { clearItems, setItemIcons } from './Items'
import User from './models/User'
import { clearOrders, fetchOrders } from './Orders'
import { clearPush, initPush } from './Push'
import { clearReseller, setReseller } from './Reseller'
import { clearRestaurants, setRestaurants } from './Restaurants'
import { clearSettings, logFeatures, setSettings } from './Settings'

const initialState = {
	token: '',
	user : {
		id          : null,
		email       : '',
		username    : '',
		display_name: '',
		created_at  : '',
		privileges  : ''
	},
}

const slice = createSlice({
	name        : 'auth',
	initialState,
	reducers    : {
		login : (state, { payload: { token, user } }) => {
			state.user  = user
			if (token) { //if not given, we cannot destroy the current token either - might happen during auth updates
				state.token = token
			}
		},
		setPrivilegesJWT: (state, { payload: jwt }) => {
			state.user.privileges = jwt
		},
		logout: () => initialState,
	},
})

//not exactly official, but enough for some organization
slice.thunks = {
	loadAuthDetails: (payload = null, skipRestaurantsWarning = false) => async dispatch => {
		if (!payload) {
			payload = await API.get('auth/details')
		}

		const reseller = payload.data.reseller || false
		dispatch(slice.actions.login({ user: payload.data.user, token: payload.data.token }))
		dispatch(setReseller(reseller))
		dispatch(initPush(payload.data.push))
		dispatch(setSettings(payload.data.user.tablet_options))
		await dispatch(setItemIcons(payload.data.icons))

		//setRestaurants() depends on the account details
		await dispatch(setAccount(payload.data.account))

		//fetchOrders() depend on the restaurant timezone set
		await dispatch(setRestaurants(payload.data.restaurants, skipRestaurantsWarning))

		//initial list of orders, to be refreshed upon user request, or via push notifications
		dispatch(fetchOrders)
	},
	/**
	 * @param phase {'start'|'end'|'both'} Which part of logout are we? Before the DELETE /auth request, or after?
	 */
	clearAuthDetails: (phase = 'both') => async (dispatch, getState) => {
		if (phase == 'start' || phase == 'both') {
			await dispatch(clearPush) //needs auth, but if the logout was caused by a deleted token, push is already gone
		}
		if (phase == 'end' || phase == 'both') {
			const supportChat = SupportChat.fromState(getState()) //comes before since it needs the account details

			//TODO: could we use persistor's purge() instead?
			await dispatch(clearAccount()) //changes what appears in /login, so needs to be awaited for
			dispatch(slice.actions.logout())

			// no need to clean printers, as that's considered data linked to the device, not to the login
			dispatch(clearRestaurants())
			dispatch(clearSettings())
			dispatch(clearReseller())
			dispatch(clearOrders())
			dispatch(clearItems())
			dispatch(clearCategories())
			dispatch(clearInvoices())

			useObjSelectorOrFetch.logout()

			//last one, so it's not re-hydrated by re-renders caused by the state changes above ¯\_(ツ)_/¯
			supportChat?.nuke()

			Sentry.setUser(null)

			sessionStorage.removeItem('thirdPartyModalShown')
		}
	},

	login: ({ account, username, password }) => async dispatch => {
		const locale = Locale.currentLang()
		const payload = await API.post('auth', { account, username, password, locale })
		dispatch(logFeatures())
		await dispatch(slice.thunks.loadAuthDetails(payload))
	},

	//FIXME it seems each state clear here causes at least one re-render
	logout: async dispatch => {
		try {
			await dispatch(slice.thunks.clearAuthDetails('start'))
			await API.deleteAndHandle('auth', HTTP_CODE.UNAUTHORIZED__401) //TODO this didn't get caught when accessing and not logged because, to my best knowledge, there's a race condition between the push 401 and the logout 401. However, this note was from before the implementation of deleteAndHandle(), so this issue might be gone
		} catch (e) {
			console.warn('Error while logging out from the API', e)
		} finally {
			await dispatch(slice.thunks.clearAuthDetails('end'))
		}
	},

	refreshLogin: refreshToken => async dispatch => {
		try {
			await dispatch(slice.thunks.clearAuthDetails('both'))

			const resp = await API.post('auth/refresh', { refreshToken })
			const token = resp.data

			await dispatch(slice.actions.login({ token, user: initialState.user }))
			await dispatch(slice.thunks.loadAuthDetails(null, true))
			window.location = '/manager' //finally, force to enter the app again
		} catch (e) {
			//probably because it took too long?
			console.error('Error while trying to use refresh token to get auth token', e)
		}
	}
}

export const isAuthenticated = ({ auth }) => !!auth.token
export const getUser         = ({ auth }) => new User(auth.user)

/** @return User */
export const useUser         = () => useSelector(({ auth }) => new User(auth.user), User.isEqual)

export const { setPrivilegesJWT } = slice.actions
export const { loadAuthDetails, clearAuthDetails, login, logout, refreshLogin } = slice.thunks
export default slice.reducer
